import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from '../i18n/useTranslation';
import api from '../api';

const FALLBACK_RATES = {
  MNT_PER_RUB: 49.21,
  MNT_PER_USD: 3575.78,
  MNT_RUB: 1 / 49.21,
  MNT_USD: 1 / 3575.78,
};

const RATES_REFRESH_MS = 60 * 60 * 1000;

let cachedRates = null;
let cachedAt = 0;
let fetchPromise = null;

function fetchRates(force = false) {
  const stale = !cachedRates || Date.now() - cachedAt > RATES_REFRESH_MS;
  if (!force && !stale && cachedRates) {
    return Promise.resolve(cachedRates);
  }
  if (fetchPromise) return fetchPromise;

  fetchPromise = api.get('/api/exchange-rates')
    .then(res => {
      cachedRates = normalizeRates(res.data);
      cachedAt = Date.now();
      return cachedRates;
    })
    .catch(() => {
      cachedRates = FALLBACK_RATES;
      cachedAt = Date.now();
      return cachedRates;
    })
    .finally(() => { fetchPromise = null; });

  return fetchPromise;
}

function normalizeRates(data) {
  const mntPerRub = data?.MNT_PER_RUB || (data?.MNT_RUB ? 1 / data.MNT_RUB : FALLBACK_RATES.MNT_PER_RUB);
  const mntPerUsd = data?.MNT_PER_USD || (data?.MNT_USD ? 1 / data.MNT_USD : FALLBACK_RATES.MNT_PER_USD);
  return {
    MNT_PER_RUB: mntPerRub,
    MNT_PER_USD: mntPerUsd,
    MNT_RUB: data?.MNT_RUB || 1 / mntPerRub,
    MNT_USD: data?.MNT_USD || 1 / mntPerUsd,
    updatedAt: data?.updatedAt,
  };
}

function convertMNT(mnt, lang, rates) {
  switch (lang) {
    case 'mn':
      return { value: mnt, symbol: '₮', locale: 'mn-MN', prefix: '' };
    case 'en':
      return {
        value: Math.round(mnt / (rates.MNT_PER_USD || FALLBACK_RATES.MNT_PER_USD)),
        symbol: '$',
        locale: 'en-US',
        prefix: 'from ',
      };
    default:
      return {
        value: Math.round(mnt / (rates.MNT_PER_RUB || FALLBACK_RATES.MNT_PER_RUB)),
        symbol: '₽',
        locale: 'ru-RU',
        prefix: 'от ',
      };
  }
}

export const useCurrency = () => {
  const { lang } = useTranslation();
  const [rates, setRates] = useState(cachedRates || FALLBACK_RATES);

  useEffect(() => {
    fetchRates().then(setRates);
    const timer = setInterval(() => {
      fetchRates(true).then(setRates);
    }, RATES_REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  const formatPrice = useCallback((mnt, opts = {}) => {
    if (!mnt && mnt !== 0) return '';
    const { showFrom = true } = opts;
    const { value, symbol, locale, prefix } = convertMNT(mnt, lang, rates);

    const formatted = new Intl.NumberFormat(locale, {
      maximumFractionDigits: 0,
    }).format(value);

    const fromPrefix = showFrom ? prefix : '';

    if (lang === 'en') {
      return `${fromPrefix}${symbol}${formatted}`;
    }
    return `${fromPrefix}${formatted} ${symbol}`;
  }, [lang, rates]);

  const formatRange = useCallback((min, max) => {
    if (!max) return formatPrice(min);

    const minConv = convertMNT(min, lang, rates);
    const maxConv = convertMNT(max, lang, rates);
    const { symbol, locale } = minConv;

    const fmt = (v) => new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(v);

    if (lang === 'en') {
      return `${symbol}${fmt(minConv.value)} – ${symbol}${fmt(maxConv.value)}`;
    }
    return `${fmt(minConv.value)} – ${fmt(maxConv.value)} ${symbol}`;
  }, [lang, rates, formatPrice]);

  const formatPartPrice = useCallback((mnt, qty) => {
    const qtyNum = parseInt(qty, 10);
    const total = qtyNum > 1 ? mnt * qtyNum : mnt;
    return formatPrice(total, { showFrom: false });
  }, [formatPrice]);

  return { formatPrice, formatRange, formatPartPrice, rates };
};
